import Phaser from "phaser";

/**
 * Retangulo do mundo onde uma camada de cenario pode desenhar. Muda a cada
 * giro/redimensionamento da tela, entao as camadas se reajustam sozinhas.
 */
export interface Band {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Quantidade de elementos para a area da faixa, respeitando os limites. Faixa
 * sem altura (a tela nao sobrou nada naquela direcao) fica vazia, senao os
 * elementos iriam parar em qualquer lugar.
 */
function populationFor(band: Band, per10k: number, min: number, max: number): number {
  const width = band.right - band.left;
  const height = band.bottom - band.top;
  if (width <= 0 || height <= 0) return 0;
  return Phaser.Math.Clamp(Math.round(((width * height) / 10_000) * per10k), min, max);
}

/**
 * Recoloca o que ficou fora da faixa nova. Sorteia de novo em vez de encostar
 * na borda: ao girar a tela a faixa encolhe muito de um lado, e encostar todo
 * mundo no limite alinharia os elementos numa linha unica. Quem rola sai dela
 * em alguns segundos, mas as estrelas ficam paradas — la a linha seria eterna.
 */
function refit(value: number, min: number, max: number): number {
  return value < min || value > max ? Phaser.Math.Between(min, max) : value;
}

interface Cloud {
  x: number;
  y: number;
  /** Nuvem mais lenta parece mais distante: parallax simples. */
  factor: number;
  image: Phaser.GameObjects.Image;
}

/**
 * Nuvens espalhadas por toda a faixa de ceu visivel. A quantidade acompanha o
 * tamanho da faixa: em tela alta (celular em pe) sobra muito ceu, e ele precisa
 * ser preenchido do mesmo jeito que na tela larga.
 */
export class CloudLayer {
  private clouds: Cloud[] = [];
  private band: Band = { left: 0, right: 0, top: 0, bottom: 0 };
  private color = 0xffffff;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly texture: string,
    private readonly depth: number,
    /** Nuvens por 10.000 px² de ceu. */
    private readonly density: number,
  ) {}

  /** Reenquadra a camada e ajusta a populacao ao novo tamanho do ceu. */
  fit(band: Band): void {
    this.band = band;
    const target = populationFor(band, this.density, 3, 30);

    while (this.clouds.length > target) this.clouds.pop()!.image.destroy();
    while (this.clouds.length < target) this.clouds.push(this.create());

    for (const cloud of this.clouds) {
      cloud.x = refit(cloud.x, band.left, band.right);
      cloud.y = refit(cloud.y, band.top, band.bottom);
      this.place(cloud);
    }
  }

  /** Empurra as nuvens para a esquerda; quem sai da faixa volta pela direita. */
  scroll(shift: number): void {
    for (const cloud of this.clouds) {
      cloud.x -= shift * cloud.factor;
      if (cloud.x < this.band.left - 16) this.recycle(cloud);
      this.place(cloud);
    }
  }

  setTint(color: number): void {
    this.color = color;
    for (const cloud of this.clouds) cloud.image.setTint(color);
  }

  private create(): Cloud {
    const image = this.scene.add.image(0, 0, this.texture).setOrigin(0.5, 0.5).setDepth(this.depth);
    image.setTint(this.color);
    return {
      x: Phaser.Math.Between(this.band.left, this.band.right),
      y: Phaser.Math.Between(this.band.top, this.band.bottom),
      factor: Phaser.Math.FloatBetween(0.1, 0.3),
      image,
    };
  }

  private recycle(cloud: Cloud): void {
    cloud.x = this.band.right + Phaser.Math.Between(8, 70);
    cloud.y = Phaser.Math.Between(this.band.top, this.band.bottom);
  }

  private place(cloud: Cloud): void {
    cloud.image.setPosition(Math.round(cloud.x), Math.round(cloud.y));
  }
}

interface Speck {
  x: number;
  y: number;
  rect: Phaser.GameObjects.Rectangle;
}

export interface SpeckleOptions {
  depth: number;
  width: number;
  height: number;
  color: number;
  /** Pontos por 10.000 px² da faixa. */
  density: number;
  max?: number;
}

/**
 * Pontinhos espalhados por uma faixa: estrelas no ceu da noite, pedriscos na
 * areia abaixo do chao. Servem para que as sobras de tela — que mudam de
 * tamanho conforme o aparelho — nao virem um bloco de cor chapada.
 */
export class SpeckleLayer {
  private specks: Speck[] = [];
  private band: Band = { left: 0, right: 0, top: 0, bottom: 0 };
  private visible = true;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly options: SpeckleOptions,
  ) {}

  fit(band: Band): void {
    this.band = band;
    const target = populationFor(band, this.options.density, 2, this.options.max ?? 80);

    while (this.specks.length > target) this.specks.pop()!.rect.destroy();
    while (this.specks.length < target) this.specks.push(this.create());

    for (const speck of this.specks) {
      speck.x = refit(speck.x, band.left, band.right);
      speck.y = refit(speck.y, band.top, band.bottom);
      this.place(speck);
    }
  }

  /** Move a camada junto com o cenario (pedriscos); 0 deixa parado (estrelas). */
  scroll(shift: number): void {
    if (shift === 0) return;
    for (const speck of this.specks) {
      speck.x -= shift;
      if (speck.x < this.band.left) {
        speck.x = this.band.right;
        speck.y = Phaser.Math.Between(this.band.top, this.band.bottom);
      }
      this.place(speck);
    }
  }

  setColor(color: number): void {
    for (const speck of this.specks) speck.rect.setFillStyle(color);
    this.options.color = color;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    for (const speck of this.specks) speck.rect.setVisible(visible);
  }

  private create(): Speck {
    const { width, height, color, depth } = this.options;
    const rect = this.scene.add
      .rectangle(0, 0, width, height, color)
      .setDepth(depth)
      .setVisible(this.visible);
    return {
      x: Phaser.Math.Between(this.band.left, this.band.right),
      y: Phaser.Math.Between(this.band.top, this.band.bottom),
      rect,
    };
  }

  private place(speck: Speck): void {
    speck.rect.setPosition(Math.round(speck.x), Math.round(speck.y));
  }
}
